import { FC } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import moment, { Moment } from 'moment';

import { renderRequiredLabel } from '@/components/FormElements/RequiredFieldLabel';
import { DATE_FORMAT } from '@/config';

import { InputProps } from '..';
import { DatePicker } from '../DatePicker';
import { FormElementProps } from '../types';

export interface DatePickersFormFieldProps
  extends Omit<InputProps, 'name'>,
    FormElementProps {
  format?: string; // format how to emit date
  displayFormat?: string; // format how to display date
  allowFreeInput?: boolean;
  minDate?: Moment;
  maxDate?: Moment;
  handleChange?: (name: string, value: string | number) => void;
  handleAccept?: (name: string, value: string | number) => void;
  handleNow?: (name: string, value: string | number) => void;
  handleClear?: (name: string) => void;
}

export const FormDateField: FC<DatePickersFormFieldProps> = (props) => {
  const {
    name,
    rules,
    defaultValue,
    handleChange,
    handleAccept,
    handleNow,
    handleClear,
    format = 'YYYY-MM-DD',
    displayFormat = DATE_FORMAT,
    label,
    ...rest
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;
  const isRequired = !!rules?.required;

  return (
    <Controller
      control={control}
      defaultValue={Boolean(defaultValue) || ''}
      name={name}
      render={({ field: { onBlur, onChange, ...restField } }: any) => (
        <DatePicker
          slotProps={{ field: { clearable: true } }}
          {...rest}
          {...restField}
          error={error?.message}
          label={renderRequiredLabel(label, isRequired)}
          format={displayFormat}
          value={restField.value ? moment(restField.value) : null}
          onChange={(value: Moment | null) => {
            const formattedValue = value ? value.format(format) : null;
            onChange(formattedValue);

            if (handleAccept) handleAccept(name, formattedValue || '');
            if (handleChange) handleChange(name, formattedValue || '');
          }}
          onNowClick={(value: Moment) => {
            if (handleNow) handleNow(name, value.format(format));
          }}
          onClearClick={() => {
            if (handleClear) handleClear(name);
          }}
        />
      )}
      rules={rules}
    />
  );
};
